/**
 * 회의록 «보내기» — 두 단계(어디로 → 받는 사람).
 *
 * ## ⛔ 이 화면이 지키는 것 — 전부 실측에서 나왔다
 *
 * ```
 * · 참석자 명단은 «조직도»에서 온다 — 전사(음성→글)에서 오지 «않는다».
 *   까닭: 전사가 이름을 틀린다(실측: 「이수연」→「이수영」). 그래서 서버가 참석자를 «빈 명단»으로
 *   만든다(`minutes-internal.routes.ts:116-118`). ⛔ 전사 이름을 «기본 선택»으로 두지 않는다.
 * · 녹음은 안 보낸다 — **고를 칸도 두지 않는다**(끌 수 있게 두면 언젠가 켜진다).
 * · 확인 문구를 두지 않는다 — 「정말 보낼까요」·「되돌릴 수 없습니다」는 사용자가 뺐다.
 * ```
 *
 * ## ★ 기본으로 켜 두는 갈래가 «없다»
 *
 * 시안은 「전자결재」가 켜진 채로 그려졌지만, 그 갈래는 기안 화면으로 넘기는 길이 아직 없다.
 * **막힌 것을 기본값으로 두면 첫 화면이 벽**이라 아무것도 안 고른 상태로 시작한다.
 *
 * ## ★★ 채널마다 «다른 일»이다 (2026-09-22 사용자·관제 확정)
 *
 * ```
 * 결재   기안 화면으로 넘긴다 — 결재선을 거기서 정한다(상신에 결재자 1명 이상이 필수).
 *        ⇒ 이 화면은 받는 사람을 «안» 고른다.
 * 메시지 받는 사람 전원이 든 **대화방 하나**를 새로 만들어 한 번에 보낸다.
 *        ⇒ 그 방이 계속 남고 받는 사람끼리 서로를 본다 — 그 사실을 화면이 적는다(사실 안내다).
 * 메일   사내 + 외부 주소. 보내기 전 **읽기 전용 미리보기**를 보여 준다.
 * ```
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, Mail, MessageSquare, Users, Paperclip } from '../../icons';
import { Badge } from '../../components/shared/Badge';
import { Button } from '../../components/shared/Button';
import { Checkbox } from '../../components/shared/Checkbox';
import { InlineNote } from '../../components/shared/InlineNote';
import { CardRadio, RadioGroup } from '../../components/shared/Radio';
import { EntityPicker } from '../../components/shared/EntityPicker';
import type { Picked, PickerPerson } from '../../components/shared/EntityPicker/entity-picker-core';
import { loadOrgPickerTree, type OrgPickerTree } from '../../lib/org-picker-tree';
import { tMinutes } from '../../lib/minutes-i18n';
// ⛔ 시각 표기·갈래 이름을 «두 벌로 만들지 않는다» — 이력이 쓰는 그것을 그대로 부른다.
import { formatDateTime } from '../../lib/base-i18n';
import { CHANNEL_KEY } from './MinutesSentHistory';
import {
  countMinutesAttachments, sendMinutes, MAX_SEND_RECIPIENTS,
  type MinutesSendAck, type MinutesSendRecord,
} from '../../api/minutes-send';
import { MinutesExternalRecipients, type ExternalRecipient } from './MinutesExternalRecipients';
import {
  buildSendText, bodyLimitFor, flattenPeople, matchAttendeeCandidates, summarizeRecipientPick,
  summarizePriorSends,
  type MinutesSendChannel, type SendTextSource,
} from './minutes-send-core';

type Step = 'dest' | 'who';

export interface MinutesSendPanelProps {
  minutesId: string;
  /** 회의록에 적힌 참석자 «이름 글자». ⛔ 사람(user_id)이 아니다 — 후보로만 쓴다. */
  attendees: readonly string[];
  /** 보낼 글자를 만드는 재료. ⛔ 전사 원문은 여기 없다(넣을 길을 만들지 않았다). */
  source: SendTextSource;
  /**
   * 보내는 길이 서버에 서 있나. `false` 면 잠근다, `null` 이면 아직 모른다(이력을 읽는 중).
   * ★ 부모가 이력을 읽어 알아낸 것을 내려 준다 — **누르기 전에** 알아야 하기 때문이다.
   */
  sendReady: boolean | null;
  /**
   * ★★★ **이미 보낸 이력** — 「또 누르는 것」을 막는 유일한 근거다 (2026-09-22 운영 결함).
   *
   * ⛔ 단추 잠금은 **한 번의 클릭 «안»**에서만 막는다. 패널을 닫았다 다시 열면 잠금이 풀려 있고,
   *   눌렀는지 확신이 없는 사람은 **한 번 더 누른다** → 방 둘 · 메일 두 통 · 이력 두 줄 · 되돌리기 없음.
   * ★ 그래서 **막지 않고 적는다**(같은 회의록을 두 번 보내는 것이 정당한 경우가 있다 — 다른 사람에게,
   *   다른 갈래로). ⛔ 되묻는 말(「정말 …」·「되돌릴 수 없습니다」)은 넣지 않는다 — 사용자 결정.
   * ⚠ 부모가 이미 읽어 둔 이력을 그대로 내려 준다 — 여기서 다시 부르면 같은 것을 두 번 읽는다.
   */
  priorSends?: readonly MinutesSendRecord[];
  onBack: () => void;
  onSent: (ack: MinutesSendAck) => void;
  /**
   * 결재 갈래 — **부모가 기안 화면으로 넘긴다.**
   *
   * ⛔ 이 부품이 `useNavigate` 를 쓰지 않는 까닭은 아래 `goApproval` 주석에 있다(한 번 넣었다가
   *   시험 열두 개가 죽어 되돌렸다). 제목·본문은 여기서 만들고 **넘기는 일은 부모가** 한다.
   */
  onApprovalHandoff: (payload: { title: string; content: string }) => void;
}

export function MinutesSendPanel({ minutesId, attendees, source, sendReady, priorSends, onBack, onSent, onApprovalHandoff }: MinutesSendPanelProps) {
  const [step, setStep] = useState<Step>('dest');
  const [channel, setChannel] = useState<MinutesSendChannel | null>(null);

  const [tree, setTree] = useState<OrgPickerTree | null>(null);
  const [treeError, setTreeError] = useState(false);
  const [picked, setPicked] = useState<Picked[]>([]);
  const [externals, setExternals] = useState<ExternalRecipient[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [attachCount, setAttachCount] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * 이 «갈래»만 아직 안 선다(서버가 CHANNEL_NOT_READY 로 답했다).
   *
   * ★ `error` 와 따로 둔다 — 붉은 오류가 아니라 **「준비 중」**으로 보여야 하고, 한 번 알았으면
   *   그 갈래의 보내기 단추를 잠가 **또 누르게 하지 않는다.** 갈래를 바꾸면 풀린다.
   */
  const [channelBlocked, setChannelBlocked] = useState(false);

  /**
   * 이미 보낸 것 — 몇 번이고 마지막이 언제인가. 판정은 `summarizePriorSends` 한 곳이다.
   * ⚠ 이력을 «아직 못 읽었거나 서버가 그 길을 안 냈으면» 빈 배열이므로 이 줄은 안 그려진다 —
   *   ⛔ 그때 「보낸 적 없다」고 «말하지는» 않는다(모르는 것을 단정하지 않는다).
   */
  const prior = useMemo(() => summarizePriorSends(priorSends ?? []), [priorSends]);

  /** 첨부 개수 — 「함께 가지 않습니다」를 적으려면 몇 개인지 알아야 한다. 실패하면 0 이라 안 적는다. */
  useEffect(() => {
    let alive = true;
    void countMinutesAttachments(minutesId).then((n) => { if (alive) setAttachCount(n); });
    return () => { alive = false; };
  }, [minutesId]);

  /**
   * 조직도는 「받는 사람」 단계에 들어갈 때 읽는다 — 3+N 호출이라 회의록을 열 때마다 부르면
   * 분당 버킷(60회)에 걸린다. 모듈 캐시가 있어 두 번째 열기는 서버를 안 부른다.
   */
  useEffect(() => {
    if (step !== 'who' || tree !== null) return;
    let alive = true;
    void loadOrgPickerTree()
      .then((t) => { if (alive) setTree(t); })
      .catch(() => { if (alive) setTreeError(true); });
    return () => { alive = false; };
  }, [step, tree]);

  /**
   * 전사 이름 → 조직도 사람. ⛔ 여기서 «고르지» 않는다 — `picked` 를 건드리지 않는다.
   * ★ `attendees` 는 부모가 넘기는 같은 배열 참조라 렌더마다 새로 만들어지지 않는다
   *   (새 배열을 만들면 이 useMemo 가 매번 다시 돌고, 그 값을 보는 effect 가 있으면 «안 끝난다»).
   */
  const allPeople = useMemo<PickerPerson[]>(
    () => (tree ? flattenPeople(tree.groups, tree.loose) : []),
    [tree],
  );
  const match = useMemo(() => matchAttendeeCandidates(attendees, allPeople), [attendees, allPeople]);

  const summary = useMemo(
    () => summarizeRecipientPick(picked, tree?.groups ?? [], match.candidates),
    [picked, tree, match.candidates],
  );

  /**
   * 보낼 글자 — 미리보기에 그리는 «바로 그것»을 요청에 싣는다(두 벌이 되지 않게).
   *
   * ★ 상한이 갈래마다 다르다 — 메시지 10,000 · 메일 1,000,000(둘 다 서버 스키마에서 확인, ajv 400).
   *   고르는 것은 `bodyLimitFor` 한곳이다. ⛔ 링크는 넣지 않는다 — 회의록은 작성자만 본다.
   */
  const bodyLimit = bodyLimitFor(channel);
  const sendText = useMemo(() => buildSendText(source, bodyLimit), [source, bodyLimit]);

  const toggleCandidate = useCallback((p: PickerPerson) => {
    setPicked((prev) => (prev.some((x) => x.kind === 'person' && x.id === p.id)
      ? prev.filter((x) => !(x.kind === 'person' && x.id === p.id))
      : [...prev, { kind: 'person' as const, id: p.id, name: p.name, sub: p.sub }]));
  }, []);

  /** 갈래를 바꾸면 «앞 갈래의 거절»을 지운다 — 남겨 두면 멀쩡한 갈래가 잠긴 채로 보인다. */
  const pickChannel = useCallback((c: MinutesSendChannel) => {
    setChannel(c);
    setChannelBlocked(false);
    setError(null);
  }, []);

  const goWho = useCallback(() => {
    setError(null);
    setStep('who');
  }, []);

  /**
   * 결재 갈래 — **기안 화면으로 넘어간다.**
   *
   * ## ⛔ 여기서 `useNavigate` 를 쓰지 «않는다» (2026-09-22 — 한 번 넣었다가 되돌렸다)
   *
   * 넣었더니 이 부품의 시험 **열두 개가 한꺼번에** 「useNavigate() may be used only in the context
   * of a <Router>」로 죽었다. 부품에 라우터를 끌어들이면 **쓰는 곳마다 Router 로 감싸야** 하고,
   * 시험은 그 감싸기를 잊기 쉽다. 이 부품은 이미 `onBack`·`onSent` 를 **콜백으로** 받고 있으니
   * 같은 결로 **부모가 넘어가게** 한다 — 부모는 이미 라우터 안이다.
   *
   * ★ 본문은 **미리보기에 그린 그 글자**다(`sendText`) — 보이는 것과 가는 것을 두 벌로 만들지 않는다.
   */
  const goApproval = useCallback(() => {
    onApprovalHandoff({ title: sendText.subject, content: sendText.body });
  }, [onApprovalHandoff, sendText]);

  const submit = useCallback(async () => {
    if (channel === null || channel === 'approval') return; // 결재는 이 길로 안 간다
    const userIds = expandPickedToUserIds(picked, tree);
    const emails = channel === 'mail' ? externals.map((e) => e.email) : [];
    if (userIds.length === 0 && emails.length === 0) {
      setError(tMinutes('minutes.send.err_no_recipient'));
      return;
    }
    /*
     * ⛔ 받는 사람 상한을 **보내기 전에** 센다 — 되돌릴 수 없는 일이라 서버 400
     *   (`TOO_MANY_RECIPIENTS`)을 맞고 나서 아는 것보다 낫다.
     */
    if (userIds.length + emails.length > MAX_SEND_RECIPIENTS) {
      setError(tMinutes('minutes.send.err_too_many', {
        n: userIds.length + emails.length, max: MAX_SEND_RECIPIENTS,
      }));
      return;
    }
    setSending(true);
    setError(null);
    try {
      const r = await sendMinutes(minutesId, {
        channel,
        user_ids: userIds,
        emails,
        subject: sendText.subject,
        body: sendText.body,
      });
      /*
       * 끝이 여섯 가지다. ⛔ 뜻이 다른 것을 한 문구로 덮지 않는다 — 특히:
       *   · 「그 갈래가 아직 안 선다」를 일반 실패로 보이면 사람이 **자기 입력을 의심한다**
       *   · 「없는·못 보는 회의록」을 「서버가 준비 중」으로 보이면 **틀린 안내**다
       *   · 「앞단(프록시·엣지)이 답했다」를 「라우트가 없다」로 덮으면 **다음 사람이 배관을 안 본다**
       */
      switch (r.kind) {
        case 'sent':
          onSent(r.ack);
          return;
        case 'route-missing':
          setError(tMinutes('minutes.send.not_ready_detail'));
          return;
        case 'not-found':
          setError(tMinutes('minutes.send.err_not_found'));
          return;
        case 'channel-not-ready':
          setChannelBlocked(true);
          return;
        case 'minutes-unavailable':
          // 「그 길이 없다」가 아니라 「지금 물어볼 수 없다」 — 나중에 되면 된다.
          setError(tMinutes('minutes.send.err_unavailable'));
          return;
        case 'gateway':
          setError(tMinutes('minutes.send.err_gateway'));
          return;
        case 'needs-setup':
          /*
           * ★ 사용자가 «고칠 수 있는» 것 — 서버 메시지가 갈 곳(개인설정 > 외부 메일 계정)을 이미 말한다.
           *   ⛔ 아래 `send-failed` 와 같은 말로 덮지 않는다: 하나는 가서 등록하면 되고 하나는 못 고친다.
           */
          setError(tMinutes('minutes.send.err_needs_setup', { msg: r.message }));
          return;
        case 'send-failed':
          setError(tMinutes('minutes.send.err_send_failed', { msg: r.message }));
          return;
        case 'body-too-large':
          // ⛔ 「본문이 너무 깁니다」는 **이 갈래에만** 붙는다 — 다른 400 과 같은 말이면 받는 사람을 고치려 든다.
          setError(tMinutes('minutes.send.err_body_too_large'));
          return;
        case 'refused':
          // 서버가 «다시 확인»해서 거절한 것 — 사유를 서버 글자 그대로 보여 준다.
          setError(tMinutes('minutes.send.refused', { msg: r.message }));
          return;
      }
    } catch (e) {
      setError(tMinutes('minutes.send.err_failed', { msg: (e as Error)?.message ?? '' }));
    } finally {
      setSending(false);
    }
  }, [channel, picked, tree, externals, minutesId, sendText, onSent]);

  return (
    <div className="msend">
      <header className="msend-head">
        <button
          type="button"
          className="msend-back"
          onClick={step === 'who' ? () => setStep('dest') : onBack}
          aria-label={tMinutes('minutes.send.back')}
        >
          <ArrowLeft size={16} aria-hidden />
        </button>
        <h3 className="msend-title">{tMinutes('minutes.send.title')}</h3>
        <span className="msend-step">
          {step === 'dest' ? tMinutes('minutes.send.step_dest') : tMinutes('minutes.send.step_who')}
        </span>
      </header>

      {/*
        ★★★ **「이미 보냈습니다」** — 단추 잠금 «밖»에서 두 번 보내는 것을 막는 자리다.

        ⛔ 잠금은 한 번의 클릭 안에서만 막는다. 이 줄은 **패널을 다시 열어도·새로 들어와도** 보인다
          (이력을 서버에서 읽은 것이라 새로고침·다른 기기에서도 같다).
        ⛔ 되묻지 않는다 — 사실만 적는다(몇 번, 마지막이 언제·어느 갈래).
        ★ 「받는 사람」을 고르는 단계에서도 보여야 한다 — 그래서 `step` 을 보지 않는다.
      */}
      {prior.count > 0 && (
        <InlineNote tone="warning" className="msend-already">
          <strong>{tMinutes('minutes.send.already_head', { n: prior.count })}</strong>
          {prior.last !== null && (
            <span className="msend-note-detail">
              {tMinutes('minutes.send.already_last', {
                channel: tMinutes(CHANNEL_KEY[prior.last.channel]),
                when: formatDateTime(prior.last.sent_at),
              })}
            </span>
          )}
        </InlineNote>
      )}

      {/* 보내는 길이 아직 없다 — 「고장」이 아니라 「상태」다. */}
      {sendReady === false && (
        <InlineNote tone="warning">
          <strong>{tMinutes('minutes.send.not_ready')}</strong>
          <span className="msend-note-detail">{tMinutes('minutes.send.not_ready_detail')}</span>
        </InlineNote>
      )}

      {step === 'dest' ? (
        <div className="msend-body">
          <RadioGroup legend={tMinutes('minutes.send.step_dest')} name="minutes-send-channel">
            <CardRadio
              name="minutes-send-channel"
              value="approval"
              checked={channel === 'approval'}
              onChange={() => pickChannel('approval')}
              title={<span className="msend-dest-t"><Badge variant="default" size="sm">{tMinutes('minutes.send.ch_approval')}</Badge> {tMinutes('minutes.send.dest_approval_t')}</span>}
              description={tMinutes('minutes.send.dest_approval_s')}
            />
            <CardRadio
              name="minutes-send-channel"
              value="message"
              checked={channel === 'message'}
              onChange={() => pickChannel('message')}
              title={<span className="msend-dest-t"><Badge variant="default" size="sm">{tMinutes('minutes.send.ch_message')}</Badge> {tMinutes('minutes.send.dest_message_t')}</span>}
              description={tMinutes('minutes.send.dest_message_s')}
            />
            <CardRadio
              name="minutes-send-channel"
              value="mail"
              checked={channel === 'mail'}
              onChange={() => pickChannel('mail')}
              title={<span className="msend-dest-t"><Badge variant="default" size="sm">{tMinutes('minutes.send.ch_mail')}</Badge> {tMinutes('minutes.send.dest_mail_t')}</span>}
              description={tMinutes('minutes.send.dest_mail_s')}
            />
          </RadioGroup>

          {/* 보내는 것 — 고를 것이 «없다». 칸이 아니라 사실을 적는다. */}
          <section className="msend-payload">
            <div className="msend-payload-label">{tMinutes('minutes.send.payload_label')}</div>
            <div className="msend-payload-row">
              <span className="msend-payload-name">{tMinutes('minutes.send.payload_body')}</span>
              <Badge variant="info" size="sm">{tMinutes('minutes.send.payload_only')}</Badge>
            </div>
            <p className="msend-payload-hint">{tMinutes('minutes.send.no_audio')}</p>
            <p className="msend-payload-hint">{tMinutes('minutes.send.payload_nolink')}</p>
            {/* 첨부가 있을 때만 — 아무 말 없으면 함께 간 줄로 읽는다. */}
            {attachCount > 0 && (
              <p className="msend-payload-hint msend-attach-note">
                <Paperclip size={13} aria-hidden /> {tMinutes('minutes.send.attach_not_sent', { n: attachCount })}
              </p>
            )}
          </section>

          <div className="msend-foot">
            {channel === 'approval' ? (
              /*
               * ★★ 결재는 **기안 화면으로 넘어간다** (2026-09-22 사용자 결정 「채널마다 다르게」).
               *   이 화면이 대신 올리지 않는다 — 상신에 결재자가 1명 이상 필요하고, 그 규칙을
               *   여기서 흉내 내면 **결재선 규칙이 두 곳**이 된다.
               * ★ 넘길 것: 제목 · 본문(요약·결정·액션) · `minutesId`. 이력은 **서버가 기안 생성 때** 적는다.
               */
              <div className="msend-foot">
                <InlineNote tone="info">{tMinutes('minutes.send.approval_handoff')}</InlineNote>
                <Button variant="primary" fullWidth onClick={goApproval}>
                  {tMinutes('minutes.send.submit_approval')}
                </Button>
              </div>
            ) : (
              <Button variant="primary" fullWidth disabled={channel === null} onClick={goWho}>
                {tMinutes('minutes.send.next')}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="msend-body">
          {/* 메시지 갈래 — 단체방이 «새로 생기고 남는다»는 사실을 적는다(위협이 아니라 안내다). */}
          {channel === 'message' && (
            <InlineNote tone="info">
              {tMinutes('minutes.send.room_notice', { n: summary.personCount })}
            </InlineNote>
          )}

          {/* 참석자 후보 — ⛔ 체크가 «꺼진 채로» 온다. 켜는 것은 사람이 한다. */}
          {match.candidates.length > 0 && (
            <section className="msend-cand">
              <div className="msend-cand-head">
                <Users size={14} aria-hidden />
                <span>{tMinutes('minutes.send.candidate')}</span>
                <Badge variant="warning" size="sm">{match.candidates.length}</Badge>
              </div>
              <p className="msend-cand-hint">{tMinutes('minutes.send.candidate_hint')}</p>
              <ul className="msend-cand-list">
                {match.candidates.map((p) => (
                  <li key={p.id}>
                    <Checkbox
                      checked={picked.some((x) => x.kind === 'person' && x.id === p.id)}
                      onChange={() => toggleCandidate(p)}
                      label={<span className="msend-cand-name">{p.name}{p.sub && <span className="msend-cand-sub">{p.sub}</span>}</span>}
                    />
                  </li>
                ))}
              </ul>
            </section>
          )}

          {match.ambiguous.length > 0 && (
            <InlineNote tone="warning">{tMinutes('minutes.send.ambiguous', { names: match.ambiguous.join(', ') })}</InlineNote>
          )}
          {match.unmatched.length > 0 && (
            <InlineNote tone="info">{tMinutes('minutes.send.unmatched', { names: match.unmatched.join(', ') })}</InlineNote>
          )}

          {/* 조직도 — ★ 트리가 «다 온 뒤에» 마운트한다. EntityPicker 의 펼침 상태가 첫 렌더의
              groups 로 한 번만 정해져서(그 파일 64-70줄), 빈 트리로 먼저 마운트하면 사람이 안 보인다. */}
          <section className="msend-org">
            <div className="msend-org-label">{tMinutes('minutes.send.org_label')}</div>
            {treeError ? (
              <InlineNote tone="danger">{tMinutes('minutes.err.load_failed')}</InlineNote>
            ) : tree === null ? (
              <div className="msend-org-loading"><Loader2 size={20} className="minutes-spin" aria-hidden /></div>
            ) : (
              <EntityPicker
                groups={tree.groups}
                loose={tree.loose}
                picked={picked}
                onPickedChange={setPicked}
              />
            )}
          </section>

          {/* 외부인 — 메일 갈래에만. 메시지·결재는 사내 사용자라야 방·결재선이 성립한다. */}
          {channel === 'mail' && <MinutesExternalRecipients value={externals} onChange={setExternals} />}

          {/*
            보내기 전 읽기 전용 미리보기 — 여기 그린 글자가 «그대로» 간다.
            ★ 두 갈래 «모두»에 있다. 링크가 없어 잘린 뒷부분을 볼 길이 아예 없으므로,
              보내기 «전에» 무엇이 가는지 보는 자리가 필요하다.
          */}
          <section className="msend-preview">
            {/* 잘린다는 사실은 펼치지 않아도 보인다 — 접어 두면 모르고 보낸다. */}
            {/*
              ⛔ 상한을 하드코딩하지 않는다 — 갈래마다 다르다(메시지 10,000 · 메일은 «상한 없음»).
              ★ `truncated` 가 참이면 상한이 있었다는 뜻이다(`null` 이면 자르지 않는다). 그래도
                타입이 `null` 을 허용하므로 그 자리를 «지어내지 않고» 그때만 그린다.
            */}
            {sendText.truncated && bodyLimit !== null && (
              <InlineNote tone="warning">
                {tMinutes('minutes.send.will_truncate', { full: sendText.fullLength, max: bodyLimit })}
              </InlineNote>
            )}
            <button type="button" className="minutes-source-toggle" onClick={() => setShowPreview((v) => !v)}>
              {showPreview ? tMinutes('minutes.send.preview_hide') : tMinutes('minutes.send.preview_show')}
            </button>
            {showPreview && (
              <div className="msend-preview-box">
                <div className="msend-preview-subject">{sendText.subject}</div>
                <pre className="msend-preview-body">{sendText.body}</pre>
                <p className="msend-payload-hint">
                  {tMinutes('minutes.send.preview_readonly')}
                  {' '}
                  {tMinutes('minutes.send.preview_length', { n: sendText.body.length })}
                </p>
              </div>
            )}
          </section>

          {/*
            ★ 「이 갈래가 아직 안 선다」는 **오류가 아니다** — 붉게 그리면 사람이 자기 입력을
              의심한다. 서버는 멀쩡하고 그 길만 아직 없다(백엔드가 판을 쪼갰다).
          */}
          {channelBlocked && (
            <InlineNote tone="warning">
              <strong>{tMinutes('minutes.send.channel_not_ready')}</strong>
              <span className="msend-note-detail">{tMinutes('minutes.send.channel_not_ready_detail')}</span>
            </InlineNote>
          )}

          {error !== null && <InlineNote tone="danger">{error}</InlineNote>}

          <div className="msend-foot">
            <span className="msend-count">
              {summary.candidateCount > 0
                ? tMinutes('minutes.send.pick_count_cand', { n: summary.personCount, c: summary.candidateCount })
                : tMinutes('minutes.send.pick_count', { n: summary.personCount })}
              {channel === 'mail' && externals.length > 0 && ` · ${tMinutes('minutes.send.pick_external', { n: externals.length })}`}
            </span>
            <Button
              variant="primary"
              fullWidth
              loading={sending}
              // ★ 그 갈래가 아직 안 선다는 답을 받았으면 «또 누르게 하지 않는다».
              disabled={sendReady === false || sending || channelBlocked}
              iconLeft={channel === 'mail' ? <Mail size={15} aria-hidden /> : <MessageSquare size={15} aria-hidden />}
              onClick={() => void submit()}
            >
              {sending
                ? tMinutes('minutes.send.sending')
                : channel === 'mail' ? tMinutes('minutes.send.submit_mail') : tMinutes('minutes.send.submit_message')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * 고른 것을 «사람 id» 로 펼친다 — 팀·부서째 고른 것을 서버에 그대로 넘기지 않는다.
 *
 * ⛔ 「팀 id 를 주면 서버가 펼치게 하자」는 안을 안 쓴다. 그러면 **화면이 센 수와 서버가 보낸 수가
 *   달라질 수 있고**(그 사이 사람이 들고 나면), 이력에 남는 명단이 화면이 보여 준 명단과 어긋난다.
 *   되돌리기가 없으니 그 어긋남은 되짚을 수 없다.
 */
export function expandPickedToUserIds(picked: ReadonlyArray<Picked>, tree: OrgPickerTree | null): string[] {
  if (tree === null) return picked.filter((p) => p.kind === 'person').map((p) => p.id);
  const ids = new Set<string>();
  const people = flattenPeople(tree.groups, tree.loose);
  const byId = new Map(people.map((p) => [p.id, p]));
  for (const p of picked) {
    if (p.kind === 'person') {
      // 쉰 계정은 고를 수 없게 잠겨 있지만, 담긴 뒤에 쉬었을 수 있어 한 번 더 본다.
      if (byId.get(p.id)?.disabled !== true) ids.add(p.id);
      continue;
    }
    for (const person of groupPeople(tree, p.id)) {
      if (person.disabled !== true) ids.add(person.id);
    }
  }
  return [...ids];
}

function groupPeople(tree: OrgPickerTree, groupId: string): PickerPerson[] {
  const walk = (groups: OrgPickerTree['groups']): PickerPerson[] => {
    for (const g of groups) {
      if (g.id === groupId) return flattenPeople([g]);
      const hit = walk((g.children ?? []) as OrgPickerTree['groups']);
      if (hit.length > 0) return hit;
    }
    return [];
  };
  return walk(tree.groups);
}
